import { Button } from '@/app/common/components/button/Button'
import { ProviderPreviewSettings } from '@/schemas/provider.schema'

import FileUpload from '@/app/common/components/FileUpload/FileUpload'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { static_errors } from '@/app/common/constants'
import { Dialog, DialogContent, DialogTrigger } from '@/shad/ui/dialog'
import { useToast } from '@/shad/ui/toast/use-toast'
import React, { useCallback, useRef, useState } from 'react'
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { useUpdatePreviewSettings } from '../hooks/useUpdatePreviewSettings'
import AdminAuthGuard from '../../../admin-auth-guard'

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  )
}

type Props = {
  provider: ProviderPreviewSettings
  programId: number
  onUpdateLogoSuccess: () => void
  providerId: string
}

const LogoSettings = (props: Props) => {
  const { programId, providerId } = props
  const [imgSrc, setImgSrc] = useState('')
  const imgRef = useRef<HTMLImageElement>(null)
  const [crop, setCrop] = useState<Crop>()
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
  const [cropResult, setCropResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const { toast } = useToast()
  const updateProviderInfoMutation = useUpdatePreviewSettings({
    providerId: providerId,
    onSuccess: () => {
      setIsOpen(false)
      setImgSrc('')
      setCropResult(null)
      setError(null)
      setPreviewOpen(false)
      setCompletedCrop(undefined)
      setCrop(undefined)
    },
    onError: () => {
      toast({
        title: 'Error',
        description: static_errors.unexpected_contact_support,
        variant: 'destructive',
      })
    },
  })

  // Fixed aspect ratio to 1:1
  const aspect = 1

  // Updated file upload handler to accept files array directly
  const handleFileUpload = (files: File[]) => {
    if (!files.length) return

    const file = files[0]
    setCropResult(null)
    setError(null)
    setCrop(undefined) // Add this
    setCompletedCrop(undefined) // Add this

    const reader = new FileReader()
    reader.addEventListener('load', () => {
      setImgSrc(reader.result?.toString() || '')
    })
    reader.readAsDataURL(file)
  }

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { width, height } = e.currentTarget

      // Initialize crop to centered 1:1 aspect ratio
      setCrop(centerAspectCrop(width, height, aspect))
    },
    []
  )

  // Validate crop size
  const validateCropSize = (crop: PixelCrop): boolean => {
    if (!imgRef.current) return false

    const image = imgRef.current
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    // Calculate actual pixel dimensions of the crop
    const actualWidth = crop.width * scaleX
    const actualHeight = crop.height * scaleY

    // Check if dimensions meet minimum requirements
    return actualWidth >= 200 && actualHeight >= 200
  }

  // Perform crop
  // Improved performCrop function that handles zoom levels
  // Improved performCrop function that handles zoom levels
  const performCrop = () => {
    if (!imgRef.current || !completedCrop) return

    // Validate crop size
    if (!validateCropSize(completedCrop)) {
      setError(
        'Crop must be at least 200x200 pixels. Please select a larger area.'
      )
      return
    }

    setError(null)

    const image = imgRef.current
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) return

    // Get the actual displayed dimensions
    const rect = image.getBoundingClientRect()

    // Calculate scaling factors more accurately
    const scaleX = image.naturalWidth / rect.width
    const scaleY = image.naturalHeight / rect.height

    // Set canvas size to the desired output size (not affected by zoom)
    const outputWidth = Math.floor(completedCrop.width * scaleX)
    const outputHeight = Math.floor(completedCrop.height * scaleY)

    canvas.width = outputWidth
    canvas.height = outputHeight

    // Don't apply devicePixelRatio scaling as it can cause issues with zoom
    // ctx.scale(pixelRatio, pixelRatio); // Remove this line

    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    // Calculate crop coordinates in natural image space
    const cropX = completedCrop.x * scaleX
    const cropY = completedCrop.y * scaleY
    const cropWidth = completedCrop.width * scaleX
    const cropHeight = completedCrop.height * scaleY

    // Draw the cropped portion
    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      outputWidth,
      outputHeight
    )

    // Convert canvas to data URL
    const result = canvas.toDataURL('image/jpeg', 0.95) // Added quality parameter
    setCropResult(result)
    setPreviewOpen(true)
  }

  // Convert data URI to Blob
  const dataURItoBlob = (dataURI: string) => {
    const byteString = atob(dataURI.split(',')[1])
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]
    const ab = new ArrayBuffer(byteString.length)
    const ia = new Uint8Array(ab)

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i)
    }

    return new Blob([ab], { type: mimeString })
  }

  // Handle saving the cropped image
  const handleSave = async () => {
    if (!cropResult) return

    // Convert the data URI to a Blob
    const blob = dataURItoBlob(cropResult)

    // Create a File object from the Blob
    const file = new File([blob], 'logo.jpg', { type: 'image/jpeg' })
    updateProviderInfoMutation.mutate({
      logo_image: file,
      program_id: programId,
    })
  }

  // Helper function to close preview and reset states
  const closePreview = () => {
    setPreviewOpen(false)
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) {
          // Reset all dialog-related states when dialog closes
          setImgSrc('')
          setCropResult(null)
          setError(null)
          setPreviewOpen(false)
          setCompletedCrop(undefined)
          setCrop(undefined)
        }
      }}
    >
      <DialogTrigger>
        <AdminAuthGuard requiredPermissions={['provider_management_edit']}>
          <Button variant="outline">Change Logo</Button>
        </AdminAuthGuard>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl overflow-y-scroll max-h-[80vh]">
        <div className="flex flex-col items-center w-full mx-auto p-4 space-y-4">
          {/* Only show the upload component when not in preview mode */}
          {!previewOpen && !imgSrc && (
            <div className="w-full">
              <FileUpload
                onUpload={(files) => {
                  const mappedFiles = files.map((file) => file.file)
                  handleFileUpload(mappedFiles)
                }}
                allowMultiple={false}
              />
            </div>
          )}

          {Boolean(imgSrc) && !previewOpen && (
            <div className="w-full border border-gray-300 rounded overflow-hidden bg-gray-100">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspect}
                className="max-w-full"
              >
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={imgSrc}
                  onLoad={onImageLoad}
                  className=""
                />
              </ReactCrop>
            </div>
          )}

          {error && <div className="text-red-500 font-medium">{error}</div>}

          {!previewOpen && imgSrc && (
            <div className="space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setPreviewOpen(false)
                  setImgSrc('')
                  setCropResult(null)
                  setError(null)
                }}
              >
                Upload new file
              </Button>
              <Button
                onClick={performCrop}
                className="mt-4"
                disabled={!completedCrop}
              >
                Preview Crop
              </Button>
            </div>
          )}

          {previewOpen && cropResult && (
            <div className="mt-6 w-full">
              <h3 className="text-lg font-semibold mb-2">Cropped Preview:</h3>
              <div className="border border-gray-300 rounded p-2 mb-4">
                <img
                  src={cropResult}
                  alt="Cropped"
                  className="max-w-full mx-auto"
                />
              </div>

              <div className="flex justify-end space-x-3 mt-4">
                <div>
                  <Button size={'lg'} variant="outline" onClick={closePreview}>
                    Back to Crop
                  </Button>
                </div>
                <AdminAuthGuard
                  requiredPermissions={['provider_management_edit']}
                >
                  <div>
                    <SubmitButton
                      onClick={handleSave}
                      status={updateProviderInfoMutation.status}
                    />
                  </div>
                </AdminAuthGuard>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default LogoSettings
